import { MAX_LOOKUP_PREFIX_CHARS, MIN_LOOKUP_CHARS, lookupPrefix, matchesPrefix } from "odyssey-shared";

/**
 * The page's side of the name lookup (ADR-068's amendment "chat commands, Discord style", item 3). The stepper
 * says which prefix a name option wants looked up as the draft changes; the lookup is sent once that prefix has
 * stood for `LOOKUP_DEBOUNCE_MS`, only when it holds `MIN_LOOKUP_CHARS` to `MAX_LOOKUP_PREFIX_CHARS` units, as the
 * lookup's schema does, and differs from the last prefix asked. The latest answer is kept and read filtered by what
 * is typed now, so an answer that arrives after the player typed on never shows a name the draft no longer matches.
 * An answer carrying a wait means the account's lookups are spent: nothing is asked until the wait ends, and then
 * the prefix still wanted is asked again.
 */

/**
 * How long a prefix must stand before it is looked up, in milliseconds.
 */
export const LOOKUP_DEBOUNCE_MS = 250;

/**
 * A name a lookup answered.
 */
export interface NameMatch {
  readonly firstName: string;
  readonly familyName: string;
}

/**
 * The timers the lookups run on: the page's own, or a test's.
 */
export interface LookupTimers {
  readonly set: (run: () => void, ms: number) => unknown;
  readonly clear: (handle: unknown) => void;
}

/**
 * What the lookups send through and run on.
 */
export interface NameLookupOptions {
  /** Sends a lookup, answering false when there is no connection ready to send it. */
  readonly send: (prefix: string) => boolean;
  /** The page's clock. */
  readonly now: () => number;
  /** The timers, the page's by default. */
  readonly timers?: LookupTimers;
}

/**
 * The page's timers, which the lookups and `/ping`'s run use unless given a test's.
 */
export const PAGE_TIMERS: LookupTimers = {
  set: (run, ms) => setTimeout(run, ms),
  clear: (handle) => clearTimeout(handle as ReturnType<typeof setTimeout>),
};

/**
 * The page's name lookups: which prefix is wanted, which was asked, the latest answer and any wait.
 *
 * @example
 * ```ts
 * const lookups = new NameLookups({ send: (prefix) => connection.sendChat({ kind: "lookup", prefix }), now: Date.now });
 * lookups.want("mi");
 * lookups.receive("mi", [{ firstName: "Mira", familyName: "Thornwood" }], 0);
 * lookups.matches("mir");
 * // [{ firstName: "Mira", familyName: "Thornwood" }]
 * ```
 */
export class NameLookups {
  private wanted: string | null = null;
  private asked: string | null = null;
  private timer: unknown = null;
  private answer: readonly NameMatch[] = [];
  private waitingUntil = 0;
  private readonly timers: LookupTimers;

  /**
   * @param options Where lookups are sent, the clock and the timers.
   */
  public constructor(private readonly options: NameLookupOptions) {
    this.timers = options.timers ?? PAGE_TIMERS;
  }

  /**
   * When the account's lookups may be asked again, on the page's clock, or 0 for no wait.
   *
   * @returns The time.
   */
  public get waitUntil(): number {
    return this.waitingUntil;
  }

  /**
   * Says which prefix the draft wants looked up now, restarting the wait before it is asked.
   *
   * @param prefix The prefix in the lookup's form (`lookupPrefix`), or null when the draft wants none.
   */
  public want(prefix: string | null): void {
    if (prefix === this.wanted) {
      return;
    }
    this.wanted = prefix;
    this.restart();
  }

  /**
   * Takes an answer: its names, kept until the next, or its wait, after which the prefix still wanted is asked.
   *
   * @param prefix The prefix it answers, as sent.
   * @param names The names it holds.
   * @param waitMs The wait until the account may look up again, 0 for an answer with names.
   * @returns True: the lookups changed.
   */
  public receive(prefix: string, names: readonly NameMatch[], waitMs: number): boolean {
    if (waitMs > 0) {
      this.waitingUntil = this.options.now() + waitMs;
      if (this.asked === lookupPrefix(prefix)) {
        this.asked = null;
      }
      this.restart();
      return true;
    }
    this.answer = names;
    return true;
  }

  /**
   * The latest answer's names whose full name starts with what is typed now.
   *
   * @param typed The name option's text as typed.
   * @returns The names, in the answer's order.
   */
  public matches(typed: string): readonly NameMatch[] {
    return this.answer.filter((name) => matchesPrefix(name.firstName, name.familyName, typed));
  }

  /**
   * Holds every ask for a wait the gateway counted against the account's lookups, which both kinds of lookup share:
   * nothing is asked until it ends, and then the prefix still wanted is asked again.
   *
   * @param waitMs The wait in milliseconds.
   */
  public holdFor(waitMs: number): void {
    this.waitingUntil = Math.max(this.waitingUntil, this.options.now() + waitMs);
    this.restart();
  }

  /**
   * Forgets what was wanted, asked and answered, but not the account's wait, so the next time a prefix is wanted it is
   * asked afresh and no earlier answer is read in its place.
   */
  public reset(): void {
    this.ended();
    this.answer = [];
  }

  /**
   * Forgets what was wanted and asked, as a connection's end must: the next connection is asked afresh.
   */
  public ended(): void {
    this.wanted = null;
    this.asked = null;
    this.clearTimer();
  }

  /**
   * Forgets everything, as a change of character must: what was wanted and asked, the latest answer and the
   * account's wait.
   */
  public forget(): void {
    this.ended();
    this.answer = [];
    this.waitingUntil = 0;
  }

  /**
   * Starts the wait before the wanted prefix is asked, or the rest of the account's wait if longer; a prefix outside
   * the lookup's schema is never asked.
   */
  private restart(): void {
    this.clearTimer();
    const prefix = this.wanted;
    if (prefix === null || prefix.length < MIN_LOOKUP_CHARS || prefix.length > MAX_LOOKUP_PREFIX_CHARS) {
      return;
    }
    const delay = Math.max(LOOKUP_DEBOUNCE_MS, this.waitingUntil - this.options.now());
    this.timer = this.timers.set(() => this.fire(), delay);
  }

  /**
   * Asks for the wanted prefix, unless it was the last asked or the account's lookups still wait.
   */
  private fire(): void {
    this.timer = null;
    const prefix = this.wanted;
    if (prefix === null || prefix === this.asked || this.options.now() < this.waitingUntil) {
      return;
    }
    if (this.options.send(prefix)) {
      this.asked = prefix;
    }
  }

  /**
   * Clears the pending timer, if any.
   */
  private clearTimer(): void {
    if (this.timer !== null) {
      this.timers.clear(this.timer);
      this.timer = null;
    }
  }
}
